import React, { useEffect, useRef, useState } from "react";

const MEMORY_KB = 65536;
const MEMORY_STEP_KB = 1024;
const MEMORY_TICK_MS = 12;

// A POST screen in the style of a late-90s BIOS. Each step waits `delay` ms, then either prints a
// line, appends to the current line (`append`), counts up the memory test, or clears the screen.
const SCRIPT = [
  { delay: 400, text: "AZ Modular BIOS v4.51PG" },
  { delay: 0, text: "Copyright (C) 1984-98, AZ Software, Inc." },
  { delay: 0, text: "" },
  { delay: 500, text: "PENTIUM-II CPU at 400MHz" },
  { delay: 300, memory: true },
  { delay: 400, text: "" },
  { delay: 200, text: "AZ Plug and Play BIOS Extension v1.0A" },
  { delay: 0, text: "Copyright (C) 1998, AZ Software, Inc." },
  { delay: 400, text: "   Detecting IDE Primary Master  ..." },
  { delay: 600, append: " PORTFOLIO HDD 2.1GB" },
  { delay: 150, text: "   Detecting IDE Primary Slave   ..." },
  { delay: 600, append: " ATAPI CD-ROM 24X" },
  { delay: 150, text: "   Detecting IDE Secondary Master..." },
  { delay: 450, append: " None" },
  { delay: 150, text: "   Detecting IDE Secondary Slave ..." },
  { delay: 450, append: " None" },
  { delay: 600, clear: true },
  { delay: 300, text: "Starting Aaron 98..." },
  { delay: 1400 },
];

const FOOTER = ["Press DEL to enter SETUP", "10/01/98-i440BX-AZ-2A69KA0BC-00"];

const BootSequence = ({ onDone }) => {
  const [lines, setLines] = useState([]);
  const [footer, setFooter] = useState(true);
  // Read through a ref so a parent re-render can't restart the script, and finishing happens once.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const finishedRef = useRef(false);
  const finish = useRef(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    onDoneRef.current();
  }).current;

  useEffect(() => {
    let cancelled = false;
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const appendToLast = (s) => setLines((ls) => [...ls.slice(0, -1), ls[ls.length - 1] + s]);

    const run = async () => {
      for (const step of SCRIPT) {
        await sleep(step.delay);
        if (cancelled) return;

        if (step.text !== undefined) {
          setLines((ls) => [...ls, step.text]);
        } else if (step.append) {
          appendToLast(step.append);
        } else if (step.memory) {
          for (let kb = 0; kb <= MEMORY_KB; kb += MEMORY_STEP_KB) {
            const line = `Memory Test :  ${kb}K`;
            setLines((ls) => (kb === 0 ? [...ls, line] : [...ls.slice(0, -1), line]));
            await sleep(MEMORY_TICK_MS);
            if (cancelled) return;
          }
          appendToLast(" OK");
        } else if (step.clear) {
          setLines([]);
          setFooter(false);
        }
      }
      finish();
    };
    run();

    return () => {
      cancelled = true;
    };
  }, [finish]);

  // Any key or click skips the POST, like hammering a key to get past it.
  useEffect(() => {
    window.addEventListener("keydown", finish);
    return () => window.removeEventListener("keydown", finish);
  }, [finish]);

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col justify-between bg-black p-6 text-[#aaaaaa] text-[1.6rem] leading-[1.15] cursor-default select-none"
      onClick={finish}
    >
      <pre className="terminal-font whitespace-pre">
        {lines.join("\n")}
        <span className="animate-pulse">_</span>
      </pre>
      {footer && <pre className="terminal-font whitespace-pre">{FOOTER.join("\n")}</pre>}
    </div>
  );
};

export default BootSequence;
